import { Box, Typography, Breadcrumbs, Link, Stack, useTheme } from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { Link as RouterLink } from 'react-router-dom';

interface PageHeaderProps {
  title: string;
  subtitle?: string;
  breadcrumbs?: Array<{ label: string; href?: string }>;
  actions?: React.ReactNode;
  homeHref?: string;
  homeLabel?: string;
}

export default function PageHeader({ title, subtitle, breadcrumbs, actions, homeHref = '/dashboard', homeLabel = 'Início' }: PageHeaderProps) {
  const theme = useTheme();

  return (
    <Box
      sx={{
        mb: 3,
        pb: 2.5,
        borderBottom: '1px solid',
        borderColor: theme.palette.divider,
      }}
    >
      <Stack spacing={1.5}>
        {breadcrumbs && breadcrumbs.length > 0 && (
          <Breadcrumbs
            separator={<NavigateNextIcon fontSize="small" sx={{ color: theme.palette.text.disabled }} />}
            aria-label="breadcrumb"
            sx={{
              '& .MuiBreadcrumbs-separator': {
                mx: 0.5
              }
            }}
          >
            <Link
              underline="none"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                color: theme.palette.text.secondary,
                fontSize: '0.875rem',
                fontWeight: 500,
                transition: 'color 0.2s',
                '&:hover': {
                  color: theme.palette.primary.main,
                }
              }}
              component={RouterLink}
              to={homeHref}
            >
              <HomeIcon sx={{ fontSize: 18 }} />
              {homeLabel}
            </Link>
            {breadcrumbs.map((crumb, index) =>
              crumb.href ? (
                <Link
                  key={index}
                  underline="none"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    transition: 'color 0.2s',
                    '&:hover': {
                      color: theme.palette.primary.main,
                    }
                  }}
                  component={RouterLink}
                  to={crumb.href}
                >
                  {crumb.label}
                </Link>
              ) : (
                <Typography
                  key={index}
                  sx={{
                    color: theme.palette.text.primary,
                    fontSize: '0.875rem',
                    fontWeight: 600
                  }}
                >
                  {crumb.label}
                </Typography>
              )
            )}
          </Breadcrumbs>
        )}

        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
          <Box>
            <Typography
              variant="h1"
              component="h1"
              sx={{
                fontSize: { xs: '1.75rem', md: '2rem' },
                fontWeight: 700,
                color: theme.palette.text.primary,
                letterSpacing: '-0.02em',
                mb: subtitle ? 0.5 : 0,
              }}
            >
              {title}
            </Typography>

            {subtitle && (
              <Typography
                variant="body1"
                sx={{
                  color: theme.palette.text.secondary,
                  fontSize: '0.95rem',
                  fontWeight: 400,
                  lineHeight: 1.5
                }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>

          {actions && (
            <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
              {actions}
            </Box>
          )}
        </Box>
      </Stack>
    </Box>
  );
}
